/**
 * Logs Integration Page
 * Code examples and SDK integration guide for sending logs programmatically
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import ApplicationRequired from '../../components/common/ApplicationRequired';
import applicationApi from '../../services/api/application.api';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';

type CodeLanguage = 'javascript-node' | 'javascript-browser' | 'python' | 'curl' | 'java' | 'csharp' | 'php' | 'ruby';

const API_URL = import.meta.env.VITE_API_URL || 'https://api-monitoring.devskin.com/api';

export default function LogsIntegrationPage() {
    const { t } = useTranslation(['logs', 'common']);
    const selectedAppId = useAppSelector(selectCurrentApplication);
    const [selectedLanguage, setSelectedLanguage] = useState<CodeLanguage>('javascript-node');
    const [apiKey, setApiKey] = useState('');
    const [showApiKey, setShowApiKey] = useState(false);
    const [copiedCode, setCopiedCode] = useState(false);
    const [currentApp, setCurrentApp] = useState<any>(null);

    const isAllAppsSelected = !selectedAppId || selectedAppId === 'all';

    useEffect(() => {
        loadApiKey();
        if (selectedAppId) {
            loadApplicationDetails();
        }
    }, [selectedAppId]);

    const loadApplicationDetails = async () => {
        if (!selectedAppId) return;
        try {
            const response = await applicationApi.getById(selectedAppId);
            if (response.success) {
                setCurrentApp(response.data);
            }
        } catch (error) {
            console.error('Error loading application:', error);
        }
    };

    const loadApiKey = () => {
        const token = localStorage.getItem('token');
        if (token) {
            setApiKey(token);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
    };

    const getCodeExample = () => {
        const appId = selectedAppId || 'YOUR_APP_ID';
        const appName = currentApp?.name || 'Your Application';
        const key = currentApp?.api_key || apiKey || 'YOUR_API_KEY';

        const examples: Record<CodeLanguage, string> = {
            'javascript-node': `// Install axios first: npm install axios
import axios from 'axios';

// Configuration
const API_URL = '${API_URL}';
const API_KEY = '${key}';
const APP_ID = '${appId}';

// Function to send log
async function sendLog(level, message, metadata = {}) {
  try {
    const response = await axios.post(
      \`\${API_URL}/v1/logs\`,
      {
        applicationId: APP_ID,
        level: level,        // 'error', 'warning', 'info', 'debug'
        message: message,
        source: 'backend-api', // Optional: service name
        timestamp: Date.now(),
        attributes: {
          ...metadata,
          hostname: require('os').hostname(),
          pid: process.pid,
        }
      },
      {
        headers: {
          'Authorization': \`Bearer \${API_KEY}\`,
          'Content-Type': 'application/json'
        }
      }
    );

    console.log('Log sent successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to send log:', error.message);
    throw error;
  }
}

// Usage examples
sendLog('info', 'Application started successfully', {
  version: '1.0.0',
  environment: 'production'
});

sendLog('error', 'Database connection failed', {
  error: 'Connection timeout',
  host: 'db.example.com',
  port: 5432
});

sendLog('warning', 'High memory usage detected', {
  usage: '85%',
  threshold: '80%'
});`,

            'javascript-browser': `// Browser JavaScript (ES6+)

const API_URL = '${API_URL}';
const API_KEY = '${key}';
const APP_ID = '${appId}';

async function sendLog(level, message, metadata = {}) {
  try {
    const response = await fetch(\`\${API_URL}/v1/logs\`, {
      method: 'POST',
      headers: {
        'Authorization': \`Bearer \${API_KEY}\`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        applicationId: APP_ID,
        level: level,        // 'error', 'warning', 'info', 'debug'
        message: message,
        source: 'frontend',
        timestamp: Date.now(),
        attributes: {
          ...metadata,
          url: window.location.href,
          userAgent: navigator.userAgent,
          screen: {
            width: window.screen.width,
            height: window.screen.height
          }
        }
      })
    });

    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }

    const data = await response.json();
    console.log('Log sent successfully:', data);
    return data;
  } catch (error) {
    console.error('Failed to send log:', error.message);
    throw error;
  }
}

// Automatic error tracking
window.addEventListener('error', (event) => {
  sendLog('error', event.message, {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack
  });
});

// Automatic unhandled promise rejection tracking
window.addEventListener('unhandledrejection', (event) => {
  sendLog('error', \`Unhandled Promise Rejection: \${event.reason}\`, {
    promise: String(event.promise),
    reason: event.reason
  });
});

// Usage examples
sendLog('info', 'User logged in', { userId: '12345', email: 'user@example.com' });
sendLog('warning', 'Slow API response', { endpoint: '/api/users', responseTime: 3500 });
sendLog('error', 'Failed to load data', { error: 'Network timeout' });`,

            'python': `# Install requests first: pip install requests
import requests
import socket
import os
import time
import json

# Configuration
API_URL = '${API_URL}'
API_KEY = '${key}'
APP_ID = '${appId}'

def send_log(level, message, metadata=None):
    """
    Send log to monitoring platform

    Args:
        level (str): Log level - 'error', 'warning', 'info', or 'debug'
        message (str): Log message
        metadata (dict): Additional metadata
    """
    if metadata is None:
        metadata = {}

    payload = {
        'applicationId': APP_ID,
        'level': level,
        'message': message,
        'source': 'python-app',
        'timestamp': int(time.time() * 1000),  # milliseconds
        'attributes': {
            **metadata,
            'hostname': socket.gethostname(),
            'pid': os.getpid(),
            'python_version': os.sys.version
        }
    }

    headers = {
        'Authorization': f'Bearer {API_KEY}',
        'Content-Type': 'application/json'
    }

    try:
        response = requests.post(
            f'{API_URL}/v1/logs',
            json=payload,
            headers=headers,
            timeout=5
        )
        response.raise_for_status()
        print(f'Log sent successfully: {response.json()}')
        return response.json()
    except requests.exceptions.RequestException as e:
        print(f'Failed to send log: {str(e)}')
        raise

# Usage examples
send_log('info', 'Application started', {
    'version': '1.0.0',
    'environment': 'production'
})

send_log('error', 'Database connection failed', {
    'error': 'Connection refused',
    'host': 'db.example.com',
    'port': 5432
})

send_log('warning', 'High CPU usage', {
    'usage': 85.5,
    'threshold': 80.0
})

# Context manager for automatic error logging
class LogContext:
    def __init__(self, operation_name):
        self.operation_name = operation_name

    def __enter__(self):
        send_log('info', f'Starting: {self.operation_name}')
        return self

    def __exit__(self, exc_type, exc_val, exc_tb):
        if exc_type is not None:
            send_log('error', f'Failed: {self.operation_name}', {
                'error_type': exc_type.__name__,
                'error_message': str(exc_val)
            })
        else:
            send_log('info', f'Completed: {self.operation_name}')
        return False

# Usage with context manager
with LogContext('data_processing'):
    # Your code here
    pass`,

            'curl': `# Basic log submission
curl -X POST '${API_URL}/v1/logs' \\
  -H 'Authorization: Bearer ${key}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "applicationId": "${appId}",
    "level": "info",
    "message": "Test log message",
    "source": "curl-test",
    "timestamp": '$(date +%s000)',
    "attributes": {
      "hostname": "'$(hostname)'",
      "environment": "production"
    }
  }'

# Error log with metadata
curl -X POST '${API_URL}/v1/logs' \\
  -H 'Authorization: Bearer ${key}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "applicationId": "${appId}",
    "level": "error",
    "message": "Database connection failed",
    "source": "backend",
    "timestamp": '$(date +%s000)',
    "attributes": {
      "error": "Connection timeout",
      "database": "postgresql",
      "host": "db.example.com",
      "port": 5432
    }
  }'

# Batch logs (send multiple logs at once)
curl -X POST '${API_URL}/v1/logs/batch' \\
  -H 'Authorization: Bearer ${key}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "logs": [
      {
        "applicationId": "${appId}",
        "level": "info",
        "message": "Request received",
        "source": "api",
        "timestamp": '$(date +%s000)'
      },
      {
        "applicationId": "${appId}",
        "level": "info",
        "message": "Request processed",
        "source": "api",
        "timestamp": '$(date +%s000)'
      }
    ]
  }'`,

            'java': `// Add dependency to pom.xml:
// <dependency>
//     <groupId>com.squareup.okhttp3</groupId>
//     <artifactId>okhttp</artifactId>
//     <version>4.10.0</version>
// </dependency>

import okhttp3.*;
import org.json.JSONObject;
import java.io.IOException;
import java.net.InetAddress;

public class LogSender {
    private static final String API_URL = "${API_URL}";
    private static final String API_KEY = "${key}";
    private static final String APP_ID = "${appId}";
    private static final MediaType JSON = MediaType.get("application/json; charset=utf-8");

    private final OkHttpClient client = new OkHttpClient();

    public void sendLog(String level, String message, JSONObject metadata) throws IOException {
        JSONObject payload = new JSONObject();
        payload.put("applicationId", APP_ID);
        payload.put("level", level);
        payload.put("message", message);
        payload.put("source", "java-app");
        payload.put("timestamp", System.currentTimeMillis());

        JSONObject attributes = new JSONObject(metadata);
        attributes.put("hostname", InetAddress.getLocalHost().getHostName());
        attributes.put("javaVersion", System.getProperty("java.version"));
        attributes.put("pid", ProcessHandle.current().pid());

        payload.put("attributes", attributes);

        RequestBody body = RequestBody.create(payload.toString(), JSON);
        Request request = new Request.Builder()
                .url(API_URL + "/v1/logs")
                .addHeader("Authorization", "Bearer " + API_KEY)
                .addHeader("Content-Type", "application/json")
                .post(body)
                .build();

        try (Response response = client.newCall(request).execute()) {
            if (!response.isSuccessful()) {
                throw new IOException("Unexpected code " + response);
            }
            System.out.println("Log sent successfully: " + response.body().string());
        }
    }

    // Usage examples
    public static void main(String[] args) {
        LogSender logger = new LogSender();

        try {
            // Info log
            JSONObject metadata = new JSONObject();
            metadata.put("version", "1.0.0");
            metadata.put("environment", "production");
            logger.sendLog("info", "Application started", metadata);

            // Error log
            JSONObject errorMeta = new JSONObject();
            errorMeta.put("error", "NullPointerException");
            errorMeta.put("class", "com.example.Service");
            errorMeta.put("line", 42);
            logger.sendLog("error", "Unexpected error occurred", errorMeta);
        } catch (IOException e) {
            e.printStackTrace();
        }
    }
}`,

            'csharp': `// Add NuGet package: Install-Package Newtonsoft.Json
using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;
using Newtonsoft.Json;

public class LogSender
{
    private static readonly string API_URL = "${API_URL}";
    private static readonly string API_KEY = "${key}";
    private static readonly string APP_ID = "${appId}";
    private static readonly HttpClient client = new HttpClient();

    public static async Task SendLogAsync(string level, string message, object metadata = null)
    {
        var payload = new
        {
            applicationId = APP_ID,
            level = level,
            message = message,
            source = "csharp-app",
            timestamp = DateTimeOffset.UtcNow.ToUnixTimeMilliseconds(),
            attributes = new
            {
                hostname = Environment.MachineName,
                processId = System.Diagnostics.Process.GetCurrentProcess().Id,
                clrVersion = Environment.Version.ToString(),
                ...(metadata ?? new { })
            }
        };

        var json = JsonConvert.SerializeObject(payload);
        var content = new StringContent(json, Encoding.UTF8, "application/json");

        client.DefaultRequestHeaders.Clear();
        client.DefaultRequestHeaders.Add("Authorization", $"Bearer {API_KEY}");

        try
        {
            var response = await client.PostAsync($"{API_URL}/v1/logs", content);
            response.EnsureSuccessStatusCode();

            var result = await response.Content.ReadAsStringAsync();
            Console.WriteLine($"Log sent successfully: {result}");
        }
        catch (HttpRequestException ex)
        {
            Console.WriteLine($"Failed to send log: {ex.Message}");
            throw;
        }
    }

    // Usage examples
    public static async Task Main(string[] args)
    {
        // Info log
        await SendLogAsync("info", "Application started", new
        {
            version = "1.0.0",
            environment = "production"
        });

        // Error log
        try
        {
            // Some code that might fail
            throw new Exception("Something went wrong");
        }
        catch (Exception ex)
        {
            await SendLogAsync("error", "An error occurred", new
            {
                error = ex.Message,
                stackTrace = ex.StackTrace,
                source = ex.Source
            });
        }

        // Warning log
        await SendLogAsync("warning", "High memory usage detected", new
        {
            usagePercent = 85,
            threshold = 80
        });
    }
}`,

            'php': `<?php
// Configuration
define('API_URL', '${API_URL}');
define('API_KEY', '${key}');
define('APP_ID', '${appId}');

function sendLog($level, $message, $metadata = []) {
    $payload = [
        'applicationId' => APP_ID,
        'level' => $level,
        'message' => $message,
        'source' => 'php-app',
        'timestamp' => round(microtime(true) * 1000),
        'attributes' => array_merge($metadata, [
            'hostname' => gethostname(),
            'pid' => getmypid(),
            'phpVersion' => phpversion()
        ])
    ];

    $ch = curl_init(API_URL . '/v1/logs');
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_HTTPHEADER, [
        'Authorization: Bearer ' . API_KEY,
        'Content-Type: application/json'
    ]);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($payload));

    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);

    if (curl_errno($ch)) {
        error_log('Failed to send log: ' . curl_error($ch));
        curl_close($ch);
        return false;
    }

    curl_close($ch);

    if ($httpCode >= 200 && $httpCode < 300) {
        echo "Log sent successfully: $response\\n";
        return json_decode($response, true);
    } else {
        error_log("Failed to send log. HTTP $httpCode: $response");
        return false;
    }
}

// Usage examples
sendLog('info', 'Application started', [
    'version' => '1.0.0',
    'environment' => 'production'
]);

sendLog('error', 'Database query failed', [
    'error' => 'Connection lost',
    'query' => 'SELECT * FROM users',
    'duration' => 5000
]);

sendLog('warning', 'Slow query detected', [
    'query' => 'SELECT * FROM large_table',
    'duration' => 3500,
    'threshold' => 1000
]);

// Error handler integration
set_error_handler(function($errno, $errstr, $errfile, $errline) {
    sendLog('error', $errstr, [
        'file' => $errfile,
        'line' => $errline,
        'errorNumber' => $errno
    ]);
});
?>`,

            'ruby': `# Install httparty gem: gem install httparty
require 'httparty'
require 'socket'
require 'json'

# Configuration
API_URL = '${API_URL}'
API_KEY = '${key}'
APP_ID = '${appId}'

def send_log(level, message, metadata = {})
  payload = {
    applicationId: APP_ID,
    level: level,
    message: message,
    source: 'ruby-app',
    timestamp: (Time.now.to_f * 1000).to_i,
    attributes: metadata.merge({
      hostname: Socket.gethostname,
      pid: Process.pid,
      ruby_version: RUBY_VERSION
    })
  }

  response = HTTParty.post(
    "#{API_URL}/v1/logs",
    headers: {
      'Authorization' => "Bearer #{API_KEY}",
      'Content-Type' => 'application/json'
    },
    body: payload.to_json,
    timeout: 5
  )

  if response.success?
    puts "Log sent successfully: #{response.body}"
    JSON.parse(response.body)
  else
    puts "Failed to send log: #{response.code} - #{response.body}"
    nil
  end
rescue StandardError => e
  puts "Error sending log: #{e.message}"
  raise
end

# Usage examples
send_log('info', 'Application started', {
  version: '1.0.0',
  environment: 'production'
})

send_log('error', 'Database connection failed', {
  error: 'Connection refused',
  host: 'db.example.com',
  port: 5432
})

send_log('warning', 'High memory usage', {
  usage: 85.5,
  threshold: 80.0
})

# Custom logger class
class CustomLogger
  def initialize
    @app_name = 'my-app'
  end

  def log(level, message, metadata = {})
    send_log(level, message, metadata.merge(app: @app_name))
  end

  def info(message, metadata = {})
    log('info', message, metadata)
  end

  def error(message, metadata = {})
    log('error', message, metadata)
  end

  def warning(message, metadata = {})
    log('warning', message, metadata)
  end
end

# Usage with custom logger
logger = CustomLogger.new
logger.info('User logged in', { user_id: 12345 })
logger.error('Payment failed', { amount: 99.99, currency: 'USD' })`
        };

        return examples[selectedLanguage] || '';
    };

    const languages: { value: CodeLanguage; label: string; icon: string }[] = [
        { value: 'javascript-node', label: 'Node.js (axios)', icon: '🟢' },
        { value: 'javascript-browser', label: 'JavaScript (Browser)', icon: '🌐' },
        { value: 'python', label: 'Python', icon: '🐍' },
        { value: 'curl', label: 'cURL', icon: '📡' },
        { value: 'java', label: 'Java', icon: '☕' },
        { value: 'csharp', label: 'C#', icon: '💎' },
        { value: 'php', label: 'PHP', icon: '🐘' },
        { value: 'ruby', label: 'Ruby', icon: '💎' },
    ];

    // Show ApplicationRequired component when no specific app is selected
    if (isAllAppsSelected) {
        return (
            <MainLayout>
                <ApplicationRequired
                    title={t('logs:integration.applicationRequired.title', 'Application Selection Required')}
                    message={t('logs:integration.applicationRequired.message', 'Log integration requires a specific application to be selected. Please choose an application from the top menu to continue with the code examples and API configuration.')}
                />
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="p-6 max-w-7xl mx-auto relative">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                📊 {t('logs:integration.title')}
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">
                                {t('logs:integration.subtitle')}
                            </p>
                        </div>
                        <Link
                            to="/logs"
                            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                        >
                            ← {t('logs:integration.backToLogs')}
                        </Link>
                    </div>
                </div>

                {/* Quick Start Guide */}
                <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">🚀 {t('logs:integration.quickStart.title')}</h2>
                    <div className="space-y-2 text-gray-700 dark:text-gray-300">
                        <p className="flex items-start gap-2">
                            <span className="font-semibold text-indigo-600 dark:text-indigo-400">1.</span>
                            {t('logs:integration.quickStart.step1')}
                        </p>
                        <p className="flex items-start gap-2">
                            <span className="font-semibold text-indigo-600 dark:text-indigo-400">2.</span>
                            {t('logs:integration.quickStart.step2')}
                        </p>
                        <p className="flex items-start gap-2">
                            <span className="font-semibold text-indigo-600 dark:text-indigo-400">3.</span>
                            {t('logs:integration.quickStart.step3')} <Link to="/logs" className="text-indigo-600 dark:text-indigo-400 underline">{t('logs:integration.quickStart.logsPage')}</Link>
                        </p>
                    </div>
                </div>

                {/* API Configuration */}
                <div className="mb-8 p-6 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">🔑 {t('logs:integration.apiConfig.title')}</h2>

                    {/* Selected Application Display */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('logs:integration.apiConfig.application')}
                        </label>
                        <div className="px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-semibold text-gray-900 dark:text-white">{currentApp?.name || 'Loading...'}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {currentApp?.environment || '-'} • App ID: {selectedAppId ? selectedAppId.substring(0, 8) + '...' : '-'}
                                    </div>
                                </div>
                                <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
                                    Selected
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* API Key */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('logs:integration.apiConfig.apiKey')}
                        </label>
                        <div className="flex gap-2">
                            <input
                                type={showApiKey ? 'text' : 'password'}
                                value={apiKey}
                                readOnly
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm"
                            />
                            <button
                                onClick={() => setShowApiKey(!showApiKey)}
                                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                title={showApiKey ? t('logs:integration.apiConfig.hide') : t('logs:integration.apiConfig.show')}
                            >
                                {showApiKey ? '👁️' : '👁️‍🗨️'}
                            </button>
                            <button
                                onClick={() => copyToClipboard(apiKey)}
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                            >
                                📋 {copiedCode ? t('logs:integration.apiConfig.copied') : t('logs:integration.apiConfig.copy')}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t('logs:integration.apiConfig.apiKeyHint')} {apiKey.substring(0, 20)}...
                        </p>
                    </div>

                    {/* API Endpoint */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('logs:integration.apiConfig.endpoint')}
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={`${API_URL}/v1/logs`}
                                readOnly
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm"
                            />
                            <button
                                onClick={() => copyToClipboard(`${API_URL}/v1/logs`)}
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                            >
                                📋 {t('logs:integration.apiConfig.copy')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Language Selection */}
                <div className="mb-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">💻 {t('logs:integration.chooseLanguage')}</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {languages.map((lang) => (
                            <button
                                key={lang.value}
                                onClick={() => setSelectedLanguage(lang.value)}
                                className={`p-4 rounded-lg border-2 transition-all ${
                                    selectedLanguage === lang.value
                                        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 shadow-md'
                                        : 'border-gray-200 dark:border-gray-700 hover:border-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                            >
                                <div className="text-2xl mb-1">{lang.icon}</div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {lang.label}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Code Example */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">📝 Code Example</h2>
                        <button
                            onClick={() => copyToClipboard(getCodeExample())}
                            className={`px-4 py-2 rounded-lg transition-all ${
                                copiedCode
                                    ? 'bg-green-600 text-white'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            }`}
                        >
                            {copiedCode ? '✓ Copied!' : '📋 Copy Code'}
                        </button>
                    </div>
                    <div className="relative">
                        <pre className="p-6 rounded-xl bg-gray-900 text-gray-100 overflow-x-auto border border-gray-700 shadow-lg">
                            <code className="text-sm font-mono">{getCodeExample()}</code>
                        </pre>
                    </div>
                </div>

                {/* Log Levels Reference */}
                <div className="mb-8 p-6 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">📌 Log Levels</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl">🔴</span>
                                <span className="font-bold text-red-900 dark:text-red-200">error</span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Critical issues that require immediate attention (crashes, exceptions, failed operations)
                            </p>
                        </div>
                        <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl">🟡</span>
                                <span className="font-bold text-yellow-900 dark:text-yellow-200">warning</span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Potentially harmful situations that don't prevent functionality (deprecated usage, performance issues)
                            </p>
                        </div>
                        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl">🔵</span>
                                <span className="font-bold text-blue-900 dark:text-blue-200">info</span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Important business events and milestones (user actions, system events, successful operations)
                            </p>
                        </div>
                        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl">⚪</span>
                                <span className="font-bold text-gray-900 dark:text-gray-200">debug</span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Detailed diagnostic information for troubleshooting (variable values, function calls)
                            </p>
                        </div>
                    </div>
                </div>

                {/* Attributes Reference */}
                <div className="p-6 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">🏷️ Log Attributes (Metadata)</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Add custom attributes to enrich your logs with contextual information:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Common Attributes:</h3>
                            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                                <li className="flex items-start gap-2">
                                    <span className="text-indigo-600">•</span>
                                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">userId</code> - User identifier
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-indigo-600">•</span>
                                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">sessionId</code> - Session identifier
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-indigo-600">•</span>
                                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">requestId</code> - Request trace ID
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-indigo-600">•</span>
                                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">environment</code> - dev/staging/production
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-indigo-600">•</span>
                                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">version</code> - Application version
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Error-Specific:</h3>
                            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                                <li className="flex items-start gap-2">
                                    <span className="text-red-600">•</span>
                                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">error</code> - Error message
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-red-600">•</span>
                                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">stackTrace</code> - Stack trace
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-red-600">•</span>
                                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">errorCode</code> - Error code
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-red-600">•</span>
                                    <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">file/line</code> - Source location
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}
